import styled from "@emotion/styled"
import { PHeading, PText } from "@porsche-design-system/components-react"
import { spacingFluidMd, spacingFluidXl } from "@porsche-design-system/components-react/emotion"
import type { DetailsEquipmentView, EquipmentCategoryView, EquipmentView } from "@slfinrtl/ui-bff-routes"
import { Fragment } from "react"
import { defineMessages, FormattedMessage } from "react-intl"
import { Flex, Grid, GridItem } from "src/components"
import { EquipmentText } from "./EquipmentText"

const messages = defineMessages({
	noEquipment: {
		defaultMessage: "No equipment has been added yet.",
		id: "vehicleDetails.overview.equipment.noEquipment",
	},
})

interface SpecialEquipmentProps {
	view: DetailsEquipmentView
}

export const SpecialEquipment = ({ view }: SpecialEquipmentProps) => {
	return <SpecialEquipmentList categories={view.categories} />
}

const SpecialEquipmentList = ({ categories }: { categories: EquipmentCategoryView[] }) =>
	categories.length ? (
		categories.length > 1 ? (
			<CategorizedEquipmentList equipmentCategories={categories} />
		) : (
			<UncategorizedEquipmentList equipment={categories[0].equipment} />
		)
	) : (
		<NoEquipmentList />
	)

const NoEquipmentList = () => (
	<PText color="contrast-medium">
		<FormattedMessage {...messages.noEquipment} />
	</PText>
)

const CategorizedEquipmentList = ({ equipmentCategories }: { equipmentCategories: EquipmentCategoryView[] }) => {
	const equipmentColumns = splitGroups(equipmentCategories, "equipment", 1)

	return (
		<Grid $columnGap={{ base: spacingFluidMd, s: spacingFluidXl }} $rowGap={spacingFluidMd}>
			<StyledGridItem size={{ base: 12, s: 6 }}>
				{equipmentColumns.left.column.map(({ equipment, name }, key) => (
					<div key={key}>
						<PHeading size="sm">{name}</PHeading>
						{equipment.map((item, index) => (
							<EquipmentText key={index} {...item} />
						))}
					</div>
				))}
			</StyledGridItem>
			<StyledGridItem size={{ base: 12, s: 6 }}>
				{equipmentColumns.right.column.map(({ equipment, name }, key) => (
					<div key={key}>
						<PHeading size="sm">{name}</PHeading>
						{equipment.map((item, index) => (
							<EquipmentText key={index} {...item} />
						))}
					</div>
				))}
			</StyledGridItem>
		</Grid>
	)
}

const UncategorizedEquipmentList = ({ equipment }: { equipment: EquipmentView[] }) => {
	const otherEquipmentColumns = splitEquipmentItems(equipment)

	return (
		<Flex $direction="column" $justifyContent="space-between">
			{otherEquipmentColumns.map((otherEquipmentColumn, index) => (
				<Fragment key={index}>
					{otherEquipmentColumn.map((otherEquipment, key) => (
						<EquipmentText key={key} {...otherEquipment} />
					))}
				</Fragment>
			))}
		</Flex>
	)
}

const splitEquipmentItems = (equipmentList: EquipmentView[]) => {
	const max: number = Math.ceil(equipmentList.length / 2)
	return [equipmentList.slice(0, max), equipmentList.slice(max)]
}

const StyledGridItem = styled(GridItem)`
	display: flex;
	flex-direction: column;
	gap: ${spacingFluidMd};
`

interface TwoColumnLayout<T> {
	left: {
		column: T[]
		size: number
	}
	right: {
		column: T[]
		size: number
	}
}

function splitGroups(
	groups: EquipmentCategoryView[],
	itemsKey: keyof EquipmentCategoryView,
	groupBias = 0,
): TwoColumnLayout<EquipmentCategoryView> {
	const layout: TwoColumnLayout<EquipmentCategoryView> = {
		left: { column: [], size: 0 },
		right: { column: [], size: 0 },
	}
	let startCursor = 0
	let endCursor = 0

	while (startCursor + endCursor < groups.length) {
		const startCursorCategory = groups[startCursor]
		const startCursorCategoryLength = startCursorCategory[itemsKey].length + groupBias

		const endCursorCategory = groups[groups.length - 1 - endCursor]
		const endCursorCategoryLength = endCursorCategory[itemsKey].length + groupBias

		if (layout.left.size >= layout.right.size + endCursorCategoryLength) {
			// Push into right column
			layout.right.column.unshift(endCursorCategory)
			layout.right.size += endCursorCategoryLength
			endCursor++
		} else {
			// Push into left column
			layout.left.column.push(startCursorCategory)
			layout.left.size += startCursorCategoryLength
			startCursor++
		}
	}

	return layout
}
